import { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Collapse, Drawer, Grid, Tooltip } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useOfferFilterParams } from 'hooks';

import { FILTER_GROUPS, normalizeFilterValues } from '../offerFilterSchema';

import { FilterFieldControl } from './FilterFieldControl';

import './offer-filter-drawer.less';

/** Edisyon alanları sorunun kendisi olduğu için tam genişlikte; grup seçimi düğme dizisi olduğu için de. */
const isFullWidth = (field) => field.group === 'edition' || field.key === 'articleGroup';

/** İade edilmişler seçeneği kendi durum alanının altında durur; ayrı hücrede yanındaki alanla karışıyor. */
const COMPANION_FIELDS = { dahStatus: 'dahHistory', aykStatus: 'aykHistory' };

const COMPANION_KEYS = Object.values(COMPANION_FIELDS);

/** Filtre uygulanmamış olsa da açık gelen gruplar. */
const DEFAULT_OPEN_GROUPS = ['people', 'payment'];

/** Panel açıkken gövdeye eklenen sınıf; ekran klavyesi bu sınıf varken gizlenir. */
const DRAWER_OPEN_BODY_CLASS = 'offer-filter-drawer-open';

const isSameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** Üst alanı değişen bağımlı alanları (zincirleme) taslaktan siler; seçenekleri üst alanın değerine bağlıdır. */
const clearDependents = (draft, key, fields) =>
  fields.filter((field) => field.parent?.key === key).reduce((next, field) => clearDependents({ ...next, [field.key]: undefined }, field.key, fields), draft);

const countLabel = (count) => (count ? `${count} filtre uygulandı` : 'Uygulanmış filtre yok');

const FieldHint = ({ hintId }) => {
  const { formatMessage } = useIntl();
  return (
    <Tooltip
      title={`Formdaki soru: “${formatMessage({ id: hintId })}”`}
      trigger={['hover', 'focus', 'click']}
    >
      <button
        type="button"
        className="offer-filter-field__hint"
        aria-label="Formdaki soruyu göster"
      >
        <InfoCircleOutlined />
      </button>
    </Tooltip>
  );
};

const ChangedDot = () => (
  <span
    className="offer-filter-field__dot"
    aria-hidden="true"
  />
);

/** `children`: alanın altında, aynı hücrede duran eşlik eden alan (ör. iade edilmişler seçeneği). */
const FilterField = ({ field, value, isChanged, draft, onChange, children = null }) => {
  const id = `offer-filter-${field.key.replace('.', '-')}`;
  const labelId = `${id}-label`;
  const className = [
    'offer-filter-field',
    isFullWidth(field) && 'offer-filter-field--full',
    field.parent && 'offer-filter-field--dependent',
    isChanged && 'offer-filter-field--changed',
  ]
    .filter(Boolean)
    .join(' ');

  const control = (
    <FilterFieldControl
      id={id}
      labelId={labelId}
      field={field}
      value={value}
      onChange={onChange}
      draft={draft}
    />
  );

  if (field.kind === 'flag') {
    return (
      <div className={`${className} offer-filter-field--flag`}>
        {isChanged && <ChangedDot />}
        {control}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="offer-filter-field__label">
        {isChanged && <ChangedDot />}
        <label
          id={labelId}
          htmlFor={id}
        >
          {field.label}
          {isChanged && <span className="offer-filter-sr-only"> (değişti)</span>}
        </label>
        {field.hintId && <FieldHint hintId={field.hintId} />}
      </div>
      {control}
      {children}
    </div>
  );
};

/**
 * Panelin içi: açıldığı andaki uygulanmış filtrelerden bir taslak kurar, Uygula'ya kadar URL'ye dokunmaz. Her
 * açılışta yeniden kurulur; kapatılınca uygulanmamış taslak kaybolur.
 */
const FilterDrawerContent = ({ fields, appliedValues, apply, onClose, focusGroup, isMobile }) => {
  const [draft, setDraft] = useState(appliedValues);
  const [resetCount, setResetCount] = useState(0);
  const isApplyingRef = useRef(false);
  const groupRefs = useRef({});

  const groups = FILTER_GROUPS.filter((group) => fields.some((field) => field.group === group.key));
  const normalizedDraft = useMemo(() => normalizeFilterValues(draft, fields), [draft, fields]);
  const changedKeys = fields.filter((field) => !isSameValue(normalizedDraft[field.key], appliedValues[field.key])).map((field) => field.key);

  const [activeGroups, setActiveGroups] = useState(() =>
    groups
      .filter(
        (group) =>
          group.key === focusGroup || DEFAULT_OPEN_GROUPS.includes(group.key) || fields.some((field) => field.group === group.key && field.key in appliedValues)
      )
      .map((group) => group.key)
  );

  useEffect(() => {
    if (focusGroup) {
      groupRefs.current[focusGroup]?.scrollIntoView({ block: 'start' });
    }
  }, [focusGroup]);

  const renderField = (field, companion = null) => (
    <FilterField
      key={field.key}
      field={field}
      value={draft[field.key]}
      isChanged={changedKeys.includes(field.key)}
      draft={draft}
      onChange={(value) => handleChange(field.key, value)}
    >
      {companion}
    </FilterField>
  );

  const handleChange = (key, value) => setDraft((current) => clearDependents({ ...current, [key]: value }, key, fields));

  /** Taslağı değiştirir; bağımlı girdilerin kendi seçim durumu da sıfırlansın diye alanlar yeniden kurulur. */
  const resetDraft = (values) => {
    setDraft(values);
    setResetCount((count) => count + 1);
  };

  const handleApply = () => {
    if (isApplyingRef.current) {
      return;
    }
    isApplyingRef.current = true;
    apply(normalizedDraft);
    onClose();
  };

  const clearButton = (
    <Button
      type="link"
      className="gx-mb-0"
      onClick={() => resetDraft({})}
    >
      Tümünü temizle
    </Button>
  );

  const footer = (
    <div className="offer-filter-drawer__footer">
      {changedKeys.length > 0 && (
        <div
          className="offer-filter-drawer__pending"
          role="status"
        >
          {changedKeys.length} değişiklik henüz uygulanmadı
        </div>
      )}
      <div className="offer-filter-drawer__actions">
        {!isMobile && clearButton}
        <div className="offer-filter-drawer__buttons">
          <Button
            className="gx-mb-0"
            disabled={changedKeys.length === 0}
            onClick={() => resetDraft(appliedValues)}
          >
            Vazgeç
          </Button>
          <Button
            type="primary"
            className="gx-mb-0"
            onClick={handleApply}
          >
            Uygula
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="offer-filter-drawer__layout">
      <div className="offer-filter-drawer__header">
        <div>
          <h2 className="offer-filter-drawer__title">Filtreler</h2>
          <div className="offer-filter-drawer__subtitle">{countLabel(Object.keys(appliedValues).length)}</div>
        </div>
        <div className="offer-filter-drawer__header-actions">
          {isMobile && clearButton}
          <Button
            type="text"
            className="gx-mb-0"
            icon={<CloseOutlined />}
            aria-label="Kapat"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="offer-filter-drawer__body">
        <Collapse
          key={resetCount}
          ghost
          expandIconPosition="end"
          activeKey={activeGroups}
          onChange={setActiveGroups}
        >
          {groups.map((group) => {
            const groupFields = fields.filter((field) => field.group === group.key);
            const selectedCount = groupFields.filter((field) => field.key in normalizedDraft).length;
            return (
              <Collapse.Panel
                key={group.key}
                header={
                  <span
                    ref={(node) => {
                      groupRefs.current[group.key] = node;
                    }}
                    className="offer-filter-drawer__group-title"
                  >
                    {group.label}
                    {selectedCount > 0 && (
                      <span
                        className="offer-filter-drawer__group-count"
                        aria-label={`${selectedCount} alan seçili`}
                      >
                        {selectedCount}
                      </span>
                    )}
                  </span>
                }
              >
                <div className={`offer-filter-drawer__fields${group.singleColumn ? ' offer-filter-drawer__fields--single' : ''}`}>
                  {groupFields
                    .filter((field) => !COMPANION_KEYS.includes(field.key))
                    .filter((field) => !field.parent || field.parent.when(draft[field.parent.key]))
                    .map((field) => {
                      const companion = fields.find((item) => item.key === COMPANION_FIELDS[field.key]);
                      return renderField(field, companion && renderField(companion));
                    })}
                </div>
              </Collapse.Panel>
            );
          })}
        </Collapse>
      </div>
      {footer}
    </div>
  );
};

/**
 * Teklif listesinin sağdan açılan filtre paneli. Uygulanmış filtreleri URL'den okur ve Uygula'da URL'ye yazar;
 * liste sorgusu URL'den beslenir. 576 px'ten dar ekranda tam ekran açılır.
 *
 * Panel ✕, Esc ya da dış alana tıklanarak kapatılırsa uygulanmamış değişiklikler sorulmadan atılır.
 */
export const OfferFilterDrawer = ({ open, onClose, focusGroup = null }) => {
  const { fields, values, apply } = useOfferFilterParams();
  const { sm } = Grid.useBreakpoint();
  const isMobile = sm === false;
  const [openCount, setOpenCount] = useState(0);

  // Kapanış animasyonu bitmeden yeniden açılırsa da taslak uygulanmış durumdan kurulsun.
  useEffect(() => {
    if (open) {
      setOpenCount((count) => count + 1);
    }
  }, [open]);

  useEffect(() => {
    document.body.classList.toggle(DRAWER_OPEN_BODY_CLASS, open);
    return () => document.body.classList.remove(DRAWER_OPEN_BODY_CLASS);
  }, [open]);

  return (
    <Drawer
      className="offer-filter-drawer"
      placement="right"
      open={open}
      onClose={onClose}
      closable={false}
      destroyOnClose
    >
      <FilterDrawerContent
        key={openCount}
        fields={fields}
        appliedValues={values}
        apply={apply}
        onClose={onClose}
        focusGroup={focusGroup}
        isMobile={isMobile}
      />
    </Drawer>
  );
};
